import React, { useEffect, useState, useRef, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { showToast } from "../../../utils/customToast";
import { SECURITY_KEY } from "../../../globals";
import DeleteConfirmModal from "../../../Component/DeleteModal/DeleteConfirmModal";
import { FaresSkeleton } from "../../../Component/Skeleton";
import SearchableSelect from "../../../Component/SearchableSelect/SearchableSelect";
import "./Showfares.css";

const BASE_API_URL =
  process.env.REACT_APP_BASE_API_URL || "http://localhost:5000/api";

function ShowFares() {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isReceivable =
    location.state?.isReceivable ||
    searchParams.get("type") === "receivable" ||
    location.pathname.includes("receivable");

  const [allFares, setAllFares] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [partiesList, setPartiesList] = useState([]);
  const [vendorsList, setVendorsList] = useState([]);
  const [selectedVendorFilter, setSelectedVendorFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [recordsPerPage, setRecordsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState([]);
  const [selectedVehicleFilter, setSelectedVehicleFilter] = useState("");
  const [selectedPartyFilter, setSelectedPartyFilter] = useState("");
  const [selectedPackageTypeFilter, setSelectedPackageTypeFilter] = useState("all");
  const [customFaresByPkg, setCustomFaresByPkg] = useState({});
  const [rateMode, setRateMode] = useState("common"); // 'common' (default) or 'specific' (Vendor/Party)
  const [deletePackageInfo, setDeletePackageInfo] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleToggleRateMode = (mode) => {
    setRateMode(mode);
    setSelectedVendorFilter("");
    setSelectedPartyFilter("");
  };

  const [selectedCompID, setSelectedCompID] = useState("");
  const [selectedCompanyName, setSelectedCompanyName] = useState("");
  const lastCompIDRef = useRef(null);
  const BASE_REST_API_URL = process.env.REACT_APP_BASE_REST_API_URL;

  // ── Company Sync ──
  useEffect(() => {
    const sync = () => {
      const c = JSON.parse(localStorage.getItem("selectedCompany"));
      const id = c?.id || "";
      const name = c?.name || "";
      if (id && lastCompIDRef.current !== id) {
        lastCompIDRef.current = id;
        setSelectedCompID(id);
        setSelectedCompanyName(name);
      } else if (name) {
        setSelectedCompanyName(name);
      }
    };
    sync();
    window.addEventListener("companyChanged", sync);
    window.addEventListener("storage", (e) => {
      if (e.key === "selectedCompany") sync();
    });
    return () => window.removeEventListener("companyChanged", sync);
  }, []);

  useEffect(() => { fetchVehicleTypes(); }, []);

  useEffect(() => {
    if (selectedCompID) {
      if (isReceivable) {
        fetchParties(selectedCompID);
      } else {
        fetchVendors(selectedCompID);
      }
    }
  }, [selectedCompID, isReceivable]);

  const fetchVehicleTypes = async () => {
    try {
      const res = await fetch(`${BASE_API_URL}/fares/vehicle-types`);
      const j = await res.json();
      if (j.success) setVehicleTypes(j.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchParties = async (compId) => {
    try {
      const res = await fetch(`${BASE_API_URL}/fares/parties/${compId}`);
      const j = await res.json();
      if (j.success) setPartiesList(j.data || []);
    } catch (err) { console.error(err); }
  };

  const fetchVendors = async (compId) => {
    if (!compId) return;
    try {
      // 1. Try Node backend API first (Sundry Creditors)
      try {
        const res = await fetch(`${BASE_API_URL}/fares/vendors/${compId}`);
        if (res.ok) {
          const j = await res.json();
          if (j.success && Array.isArray(j.data) && j.data.length > 0) {
            setVendorsList(
              j.data.map((v) => ({
                VendorID: v.VendorID || v.HEAD_CODE,
                VendorName: (v.VendorName || v.HEADName || "").replace(/^["'“”`\s]+|["'“”`\s]+$/g, "").trim(),
              }))
            );
            return;
          }
        }
      } catch (_) {}

      // 2. Fallback to ASP.NET REST API SelectData.aspx (GroupCODE = 23 for Sundry Creditors)
      if (BASE_REST_API_URL) {
        const data = new URLSearchParams();
        data.append("SecurityKey", SECURITY_KEY);
        data.append("TableName", "HEAD_MAST");
        data.append("WhereCondition", `CompID=${compId} AND GroupCODE=23`);
        data.append("*", "*");

        const res = await fetch(`${BASE_REST_API_URL}/SelectData.aspx`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: data.toString(),
        });
        const j = await res.json();
        if (Array.isArray(j.Response)) {
          const vendors = j.Response
            .filter((v) => v.HEADName && v.HEADName.trim() !== "")
            .map((v) => ({
              VendorID: v.HEAD_CODE,
              VendorName: v.HEADName.replace(/^["'“”`\s]+|["'“”`\s]+$/g, "").trim(),
            }))
            .sort((a, b) => a.VendorName.localeCompare(b.VendorName));
          setVendorsList(vendors);
        }
      }
    } catch (err) {
      console.error("fetchVendors error:", err);
    }
  };

  useEffect(() => {
    if (selectedCompID) fetchAllFares(selectedCompID);
  }, [selectedCompID, isReceivable]);

  const fetchAllFares = async (compId) => {
    setLoading(true);
    try {
      if (isReceivable) {
        try {
          let jCust = null;
          try {
            const resCust = await fetch(`${BASE_API_URL}/fares/receivable-custom-all/${compId}`);
            if (resCust.ok) jCust = await resCust.json();
          } catch (_) {}

          if ((!jCust || !jCust.success) && BASE_API_URL !== "http://localhost:5000/api") {
            try {
              const resCustLocal = await fetch(`http://localhost:5000/api/fares/receivable-custom-all/${compId}`);
              if (resCustLocal.ok) jCust = await resCustLocal.json();
            } catch (_) {}
          }

          if (jCust && jCust.success && Array.isArray(jCust.data)) {
            const map = {};
            jCust.data.forEach((cf) => {
              const k = (cf.PackageName || "").trim();
              if (!map[k]) map[k] = [];
              map[k].push(cf);
            });
            setCustomFaresByPkg(map);
          } else {
            setCustomFaresByPkg({});
          }
        } catch (_) {
          setCustomFaresByPkg({});
        }

        const res = await fetch(`${BASE_API_URL}/fares/all/${compId}?fareType=receivable`);
        const j = await res.json();
        if (j.success) {
          setAllFares(j.data || []);
        } else {
          showToast.error(j.message || "Failed to load receivable fares.");
        }
        return;
      }

      // ── Payable Fares (from Node API or ASP.NET REST API fallback) ──
      if (!isReceivable) {
        try {
          let jCust = null;
          try {
            const resCust = await fetch(`${BASE_API_URL}/fares/payable-custom-all/${compId}`);
            if (resCust.ok) jCust = await resCust.json();
          } catch (_) {}

          if ((!jCust || !jCust.success) && BASE_API_URL !== "http://localhost:5000/api") {
            try {
              const resCustLocal = await fetch(`http://localhost:5000/api/fares/payable-custom-all/${compId}`);
              if (resCustLocal.ok) jCust = await resCustLocal.json();
            } catch (_) {}
          }

          if (jCust && jCust.success && Array.isArray(jCust.data)) {
            const map = {};
            jCust.data.forEach((cf) => {
              const k = (cf.PackageName || "").trim();
              if (!map[k]) map[k] = [];
              map[k].push(cf);
            });
            setCustomFaresByPkg(map);
          } else {
            setCustomFaresByPkg({});
          }
        } catch (_) {
          setCustomFaresByPkg({});
        }
      }

      try {
        const resNode = await fetch(`${BASE_API_URL}/fares/all/${compId}?fareType=payable`);
        const jNode = await resNode.json();
        if (jNode.success && Array.isArray(jNode.data) && jNode.data.length > 0) {
          const commonOnly = isReceivable
            ? jNode.data
            : jNode.data.filter((f) => !f.HEAD_CODE || Number(f.HEAD_CODE) === 0);
          setAllFares(commonOnly);
          return;
        }
      } catch (_) { }

      // Fallback: ASP.NET REST API for Payable Fares
      const faresReq = new URLSearchParams();
      faresReq.append("SecurityKey", SECURITY_KEY);
      faresReq.append("TableName", "Fares");
      faresReq.append("WhereCondition", `CompID=${compId} AND (HEAD_CODE = 0 OR HEAD_CODE IS NULL)`);
      faresReq.append("*", "*");

      const pmReq = new URLSearchParams();
      pmReq.append("SecurityKey", SECURITY_KEY);
      pmReq.append("TableName", "PackageMaster");
      pmReq.append("WhereCondition", `CompID=${compId}`);
      pmReq.append("*", "*");

      const [faresRes, pmRes] = await Promise.all([
        fetch(`${BASE_REST_API_URL}/SelectData.aspx`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: faresReq.toString(),
        }),
        fetch(`${BASE_REST_API_URL}/SelectData.aspx`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: pmReq.toString(),
        }),
      ]);

      const jFares = await faresRes.json();
      const jPm = await pmRes.json();

      const rawFares = (Array.isArray(jFares.Response) ? jFares.Response : []).filter(
        (f) => !f.HEAD_CODE || Number(f.HEAD_CODE) === 0
      );
      const rawPm = Array.isArray(jPm.Response) ? jPm.Response : [];

      const pmMap = {};
      rawPm.forEach((p) => {
        if (p.Id != null && p.PackageName) {
          pmMap[String(p.Id)] = {
            name: p.PackageName,
            type: p.PackageType !== undefined && p.PackageType !== null ? Number(p.PackageType) : 0,
          };
        }
      });

      const merged = rawFares.map((f) => {
        const pmInfo = pmMap[String(f.PackageID)] || {};
        return {
          ...f,
          PackageName: f.PackageName || pmInfo.name || `Package #${f.PackageID || f.ID}`,
          PackageType: f.PackageType !== undefined && f.PackageType !== null ? Number(f.PackageType) : (pmInfo.type ?? 0),
        };
      });

      setAllFares(merged);
    } catch (err) {
      console.error(err);
      showToast.error("Failed to load fares.");
    } finally {
      setLoading(false);
    }
  };

  const vtName = (id, fallback) => {
    if (fallback) return fallback;
    const v = vehicleTypes.find((x) => String(x.ID) === String(id));
    return v ? v.VTYPE : id ? `Type ${id}` : "—";
  };

  const fmt = (val) => {
    if (val == null || val === "") return "—";
    const n = parseFloat(val);
    return isNaN(n) ? val : n.toLocaleString("en-IN");
  };

  // Combined list of Sundry Creditor vendors (including any found in customFaresByPkg)
  const allAvailableVendors = useMemo(() => {
    const map = new Map();
    vendorsList.forEach((v) => {
      const vId = String(v.VendorID ?? v.HEAD_CODE ?? "");
      const vName = (v.VendorName ?? v.HEADName ?? "").replace(/^["'“”`\s]+|["'“”`\s]+$/g, "").trim();
      if (vId && vName) map.set(vId, vName);
    });

    Object.values(customFaresByPkg).forEach((faresArr) => {
      if (Array.isArray(faresArr)) {
        faresArr.forEach((cf) => {
          const vId = String(cf.VendorID ?? cf.HEAD_CODE ?? "");
          const vName = (cf.VendorName ?? "").replace(/^["'“”`\s]+|["'“”`\s]+$/g, "").trim();
          if (vId && vName && !map.has(vId)) {
            map.set(vId, vName);
          }
        });
      }
    });

    return Array.from(map.entries())
      .map(([id, name]) => ({ VendorID: id, VendorName: name }))
      .sort((a, b) => a.VendorName.localeCompare(b.VendorName));
  }, [vendorsList, customFaresByPkg]);

  const vendorSelectOptions = useMemo(() => {
    return [
      { value: "", label: "All Vendors (Sundry Creditors)" },
      ...allAvailableVendors.map((v) => ({
        value: String(v.VendorID),
        label: v.VendorName,
        group: "Sundry Creditors",
      })),
    ];
  }, [allAvailableVendors]);

  // Combined list of Sundry Debtor parties (including any found in customFaresByPkg)
  const allAvailableParties = useMemo(() => {
    const map = new Map();
    partiesList.forEach((p) => {
      const pId = String(p.PartyID ?? "");
      const pName = (p.PartyName ?? "").replace(/^["'“”`\s]+|["'“”`\s]+$/g, "").trim();
      if (pId && pName) map.set(pId, pName);
    });

    Object.values(customFaresByPkg).forEach((faresArr) => {
      if (Array.isArray(faresArr)) {
        faresArr.forEach((cf) => {
          const pId = String(cf.PartyID ?? "");
          const pName = (cf.PartyName ?? "").replace(/^["'“”`\s]+|["'“”`\s]+$/g, "").trim();
          if (pId && pName && !map.has(pId)) {
            map.set(pId, pName);
          }
        });
      }
    });

    return Array.from(map.entries())
      .map(([id, name]) => ({ PartyID: id, PartyName: name }))
      .sort((a, b) => a.PartyName.localeCompare(b.PartyName));
  }, [partiesList, customFaresByPkg]);

  const partySelectOptions = useMemo(() => {
    return [
      { value: "", label: "All Parties (Sundry Debtors)" },
      ...allAvailableParties.map((p) => ({
        value: String(p.PartyID),
        label: p.PartyName,
        group: "Sundry Debtors",
      })),
    ];
  }, [allAvailableParties]);

  const grouped = useMemo(() => {
    const isSpecificMode = rateMode === "specific";
    const selectedTargetId = isReceivable ? selectedPartyFilter : selectedVendorFilter;

    // 1. Process common baseline fares
    const commonPkgMap = {};
    allFares.forEach((f) => {
      const n = (f.PackageName || "").trim();
      if (!n) return;
      if (!isReceivable && f.HEAD_CODE && Number(f.HEAD_CODE) > 0) return;
      if (isReceivable && f.PartyID && Number(f.PartyID) > 0) return;

      if (!commonPkgMap[n]) {
        commonPkgMap[n] = {
          key: n,
          name: n,
          packageType: f.PackageType !== undefined && f.PackageType !== null ? Number(f.PackageType) : 0,
          fares: [],
        };
      }
      if (!commonPkgMap[n].fares.some((existing) => String(existing.Vtype) === String(f.Vtype))) {
        commonPkgMap[n].fares.push(f);
      }
    });

    // 2. Include packages that exist only in custom overrides
    if (customFaresByPkg) {
      Object.keys(customFaresByPkg).forEach((pkgName) => {
        const n = pkgName.trim();
        if (!n || commonPkgMap[n]) return;
        const overrides = customFaresByPkg[n] || [];
        if (overrides.length === 0) return;
        commonPkgMap[n] = {
          key: n,
          name: n,
          packageType: overrides[0].PackageType ?? 0,
          fares: [],
        };
      });
    }

    let result = [];

    if (!isSpecificMode) {
      // ── COMMON MODE: Show ONLY Common Baseline Packages (no vendor/party overrides) ──
      result = Object.values(commonPkgMap).map((pkg) => {
        let filteredFares = pkg.fares;
        if (selectedVehicleFilter) {
          filteredFares = filteredFares.filter((f) => String(f.Vtype) === String(selectedVehicleFilter));
        }
        return {
          ...pkg,
          fares: filteredFares.sort((a, b) =>
            vtName(a.Vtype, a.VehicleTypeName).localeCompare(vtName(b.Vtype, b.VehicleTypeName))
          ),
          hasSpecificOverride: false,
        };
      });

      if (selectedVehicleFilter) {
        result = result.filter((p) => p.fares.length > 0);
      }
    } else {
      // ── SPECIFIC MODE (Vendor in Payable, Party in Receivable) ──
      // Show ONLY custom rates (no common rates mixed in)
      const pkgNamesWithOverrides = Object.keys(customFaresByPkg || {});
      result = pkgNamesWithOverrides.map((pkgName) => {
        const overridesForPkg = customFaresByPkg[pkgName] || [];
        const specificOverrides = selectedTargetId
          ? overridesForPkg.filter((cf) =>
              isReceivable
                ? String(cf.PartyID) === String(selectedTargetId)
                : String(cf.VendorID || cf.HEAD_CODE) === String(selectedTargetId)
            )
          : overridesForPkg;

        if (specificOverrides.length === 0) return null;

        let filteredFares = specificOverrides;
        if (selectedVehicleFilter) {
          filteredFares = filteredFares.filter((f) => String(f.Vtype) === String(selectedVehicleFilter));
        }
        if (filteredFares.length === 0) return null;

        const sample = specificOverrides[0];
        return {
          key: pkgName,
          name: pkgName,
          packageType: sample.PackageType ?? (commonPkgMap[pkgName]?.packageType ?? 0),
          fares: filteredFares.sort((a, b) =>
            vtName(a.Vtype, a.VehicleTypeName).localeCompare(vtName(b.Vtype, b.VehicleTypeName))
          ),
          hasSpecificOverride: true,
        };
      }).filter(Boolean);
    }

    // Filter by package type (Local / Outstation)
    if (selectedPackageTypeFilter !== "all") {
      const targetType = Number(selectedPackageTypeFilter);
      result = result.filter((p) => Number(p.packageType ?? 0) === targetType);
    }

    // Filter by search query (Package name, vendor/party name, or vehicle name)
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((p) => {
        if (p.name.toLowerCase().includes(q)) return true;
        const overrides = customFaresByPkg[p.name] || [];
        if (
          isReceivable &&
          overrides.some((cf) => (cf.PartyName || "").toLowerCase().includes(q))
        ) {
          return true;
        }
        if (
          !isReceivable &&
          overrides.some((cf) => (cf.VendorName || "").toLowerCase().includes(q))
        ) {
          return true;
        }
        return p.fares.some((f) =>
          vtName(f.Vtype, f.VehicleTypeName).toLowerCase().includes(q)
        );
      });
    }

    return result;
  }, [
    allFares,
    customFaresByPkg,
    rateMode,
    selectedVendorFilter,
    selectedPartyFilter,
    selectedVehicleFilter,
    selectedPackageTypeFilter,
    searchQuery,
    isReceivable,
    vehicleTypes,
  ]);

  // Statistics for top KPI cards
  const stats = useMemo(() => {
    const totalPkgs = grouped.length;
    const localPkgs = grouped.filter((g) => g.packageType === 0).length;
    const outstationPkgs = grouped.filter((g) => g.packageType === 1).length;
    const totalRates = grouped.reduce((acc, g) => acc + g.fares.length, 0);
    const uniqueParties = isReceivable
      ? new Set(
          Object.values(customFaresByPkg)
            .flat()
            .map((cf) => cf.PartyID)
            .filter(Boolean)
        ).size
      : 0;
    const uniqueVendors = !isReceivable
      ? new Set(
          Object.values(customFaresByPkg)
            .flat()
            .map((cf) => cf.VendorID || cf.HEAD_CODE)
            .filter(Boolean)
        ).size
      : 0;

    return {
      totalPkgs,
      localPkgs,
      outstationPkgs,
      totalRates,
      uniqueParties,
      uniqueVendors,
    };
  }, [grouped, isReceivable, customFaresByPkg]);

  const totalPages = Math.ceil(grouped.length / recordsPerPage) || 1;
  const visible = grouped.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedVehicleFilter, selectedPartyFilter, selectedPackageTypeFilter, selectedVendorFilter]);

  // Auto-expand packages when vendor or party filter is active so custom rates are immediately visible
  useEffect(() => {
    if ((selectedVendorFilter || selectedPartyFilter) && grouped.length > 0) {
      setExpanded(grouped.map((g) => g.key));
    }
  }, [selectedVendorFilter, selectedPartyFilter]);

  const toggle = (key) =>
    setExpanded((p) => (p.includes(key) ? p.filter((x) => x !== key) : [...p, key]));

  const confirmDel = (pkg, e) => {
    e.stopPropagation();
    setDeletePackageInfo({ packageName: pkg.name });
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (isDeleting) return;
    if (!deletePackageInfo || !selectedCompID) return;
    const { packageName } = deletePackageInfo;
    try {
      setIsDeleting(true);
      let url = `${BASE_API_URL}/fares/delete-package/${selectedCompID}/${encodeURIComponent(packageName)}`;
      if (isReceivable) {
        url += `?fareType=receivable`;
      }
      const r = await fetch(url, { method: "DELETE" });
      const j = await r.json();
      if (j.success) {
        showToast.delete("Package deleted successfully.");
        fetchAllFares(selectedCompID);
        setDeleteModalOpen(false);
      } else {
        showToast.error(j.message || "Delete failed.");
      }
    } catch {
      showToast.error("Delete failed.");
    } finally { 
      setIsDeleting(false);
      setDeletePackageInfo(null); 
    }
  };

  const edit = (pkg, e) => {
    e.stopPropagation();
    const addUrl = isReceivable ? "/fares?type=receivable" : "/fares";
    navigate(addUrl, {
      state: {
        editPackageName: pkg.name,
        isReceivable,
      },
    });
  };

  const addUrl = isReceivable ? "/fares?type=receivable" : "/fares";

  return (
    <div className="dashboard-wrapper fares-dashboard-container">
      {/* 1. Header & Breadcrumbs matching Dashboard */}
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div className="dashboard-breadcrumb mb-0">
          <span>Master &gt; Packages &amp; Fares &gt;</span>
          <span className="text-dark fw-bold">
            {isReceivable ? "Receivable Packages & Fares" : "Payable Packages & Fares"}
          </span>
        </div>
      </div>

      {/* 2. Top KPI Stat Cards Row (Dashboard Theme) */}
      <div className="sf-stat-grid mb-2">
        <div className="stat-item-card">
          <div className="stat-item-title">Total Packages</div>
          <div className="stat-item-value-row">
            <span className="stat-item-number">{stats.totalPkgs}</span>
            <span className="stat-item-sub gray">Active</span>
          </div>
        </div>

        <div className="stat-item-card">
          <div className="stat-item-title">Local (Day Wise)</div>
          <div className="stat-item-value-row">
            <span className="stat-item-number" style={{ color: "#008b8b" }}>{stats.localPkgs}</span>
            <span className="stat-item-sub green">Local</span>
          </div>
        </div>

        <div className="stat-item-card">
          <div className="stat-item-title">Outstation (KM Wise)</div>
          <div className="stat-item-value-row">
            <span className="stat-item-number" style={{ color: "#1d4ed8" }}>{stats.outstationPkgs}</span>
            <span className="stat-item-sub gray">Running</span>
          </div>
        </div>

        <div className="stat-item-card">
          <div className="stat-item-title">Vehicle Rates</div>
          <div className="stat-item-value-row">
            <span className="stat-item-number">{stats.totalRates}</span>
            <span className="stat-item-sub gray">Configured</span>
          </div>
        </div>

        {rateMode === "specific" ? (
          isReceivable ? (
            <div className="stat-item-card">
              <div className="stat-item-title">Custom Parties</div>
              <div className="stat-item-value-row">
                <span className="stat-item-number" style={{ color: "#16a34a" }}>{stats.uniqueParties}</span>
                <span className="stat-item-sub gray">Sundry Debtors</span>
              </div>
            </div>
          ) : (
            <div className="stat-item-card">
              <div className="stat-item-title">Custom Vendors</div>
              <div className="stat-item-value-row">
                <span className="stat-item-number" style={{ color: "#008b8b" }}>{stats.uniqueVendors}</span>
                <span className="stat-item-sub gray">Sundry Creditors</span>
              </div>
            </div>
          )
        ) : (
          <div className="stat-item-card">
            <div className="stat-item-title">View Mode</div>
            <div className="stat-item-value-row">
              <span className="stat-item-number" style={{ color: "#008b8b", fontSize: "16px" }}>Common</span>
              <span className="stat-item-sub green">Baseline</span>
            </div>
          </div>
        )}
      </div>

      {/* 3. Filter & Action Toolbar (Ledger Theme Color) */}
      <div className="filter-bar-card sf-toolbar-card">
        <div className="d-flex align-items-center gap-2 flex-wrap flex-grow-1">
          <Link to={addUrl} state={{ isReceivable }}>
            <button type="button" className="new_ledger_btn sf-primary-btn">
              <i className="fa-solid fa-plus me-1"></i>{" "}
              {isReceivable ? "Add Receivable Package" : "Add Package"}
            </button>
          </Link>

          {/* Rate Mode Toggle: Common (default) vs Vendor / Party */}
          <div className="chart-tabs-wrapper sf-type-tabs">
            <button
              type="button"
              className={`chart-tab-btn ${rateMode === "common" ? "active sf-tab-local" : ""}`}
              onClick={() => handleToggleRateMode("common")}
              title="Show Common Baseline Packages"
            >
              <i className="fa-solid fa-layer-group me-1"></i> Common
            </button>
            <button
              type="button"
              className={`chart-tab-btn ${rateMode === "specific" ? "active sf-tab-outstation" : ""}`}
              onClick={() => handleToggleRateMode("specific")}
              title={isReceivable ? "Show Party-Specific Rates" : "Show Vendor-Specific Rates"}
            >
              <i className={`fa-solid ${isReceivable ? "fa-user-group" : "fa-user-tag"} me-1`}></i>{" "}
              {isReceivable ? "Party" : "Vendor"}
            </button>
          </div>

          {/* Local / Outstation Filter Toggle Tabs */}
          <div className="chart-tabs-wrapper sf-type-tabs">
            <button
              type="button"
              className={`chart-tab-btn ${selectedPackageTypeFilter === "all" ? "active" : ""}`}
              onClick={() => setSelectedPackageTypeFilter("all")}
            >
              All
            </button>
            <button
              type="button"
              className={`chart-tab-btn ${selectedPackageTypeFilter === "0" ? "active sf-tab-local" : ""}`}
              onClick={() => setSelectedPackageTypeFilter("0")}
            >
              <i className="fa-solid fa-sun me-1"></i> Local
            </button>
            <button
              type="button"
              className={`chart-tab-btn ${selectedPackageTypeFilter === "1" ? "active sf-tab-outstation" : ""}`}
              onClick={() => setSelectedPackageTypeFilter("1")}
            >
              <i className="fa-solid fa-road me-1"></i> Outstation
            </button>
          </div>

          {/* Specific Mode: Show Party/Vendor Selector */}
          {rateMode === "specific" && (
            isReceivable ? (
              <div className="d-flex align-items-center gap-1 sf-vendor-filter-wrap">
                <span className="sf-filter-label">Party:</span>
                <div style={{ width: "220px" }}>
                  <SearchableSelect
                    options={partySelectOptions}
                    value={selectedPartyFilter}
                    onChange={(opt) => {
                      const val = opt ? (typeof opt === "object" ? opt.value : opt) : "";
                      setSelectedPartyFilter(val);
                    }}
                    placeholder="All Parties (Sundry Debtors)"
                    isClearable={true}
                    height="26px"
                  />
                </div>
              </div>
            ) : (
              <div className="d-flex align-items-center gap-1 sf-vendor-filter-wrap">
                <span className="sf-filter-label">Vendor:</span>
                <div style={{ width: "220px" }}>
                  <SearchableSelect
                    options={vendorSelectOptions}
                    value={selectedVendorFilter}
                    onChange={(opt) => {
                      const val = opt ? (typeof opt === "object" ? opt.value : opt) : "";
                      setSelectedVendorFilter(val);
                    }}
                    placeholder="All Vendors (Sundry Creditors)"
                    isClearable={true}
                    height="26px"
                  />
                </div>
              </div>
            )
          )}

          <div className="d-flex align-items-center gap-1">
            <span className="sf-filter-label">Vehicle:</span>
            <select
              className="filter-select"
              value={selectedVehicleFilter}
              onChange={(e) => setSelectedVehicleFilter(e.target.value)}
            >
              <option value="">All Vehicles</option>
              {vehicleTypes.map((vt) => (
                <option key={vt.ID} value={vt.ID}>
                  {vt.VTYPE}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Right side search & controls */}
        <div className="d-flex align-items-center gap-2">
          <div className="sf-search-wrap">
            <i className="fa-solid fa-magnifying-glass sf-search-icon"></i>
            <input
              type="search"
              className="filter-input sf-search-input"
              placeholder={isReceivable ? "Search party, package..." : "Search packages..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="d-flex align-items-center gap-1">
            <span className="sf-filter-label">Show:</span>
            <select
              className="filter-select"
              style={{ width: "55px" }}
              value={recordsPerPage}
              onChange={(e) => setRecordsPerPage(Number(e.target.value))}
            >
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="15">15</option>
              <option value="25">25</option>
            </select>
          </div>

          <button
            type="button"
            className="filter-btn-reset sf-refresh-btn"
            onClick={() => {
              if (selectedCompID) {
                fetchAllFares(selectedCompID);
                if (isReceivable) fetchParties(selectedCompID);
                else fetchVendors(selectedCompID);
              }
            }}
            title="Refresh Data"
          >
            <i className={`fa-solid fa-rotate ${loading ? "fa-spin" : ""}`}></i>
          </button>
        </div>
      </div>

      {/* 4. Main Card Container with Table */}
      <div className="dash-card sf-main-card">
        {loading ? (
          <FaresSkeleton />
        ) : visible.length === 0 ? (
          <div className="sf-empty">
            <i className="fa-solid fa-folder-open"></i>
            <span>No packages found matching your criteria.</span>
            {(searchQuery || selectedVehicleFilter || selectedPartyFilter || selectedVendorFilter || selectedPackageTypeFilter !== "all") && (
              <button
                type="button"
                className="filter-btn-reset mt-2"
                onClick={() => {
                  setSearchQuery("");
                  setSelectedVehicleFilter("");
                  setSelectedPartyFilter("");
                  setSelectedVendorFilter("");
                  setSelectedPackageTypeFilter("all");
                }}
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="sf-table-container">
              <table className="dash-pixel-table sf-table">
                <thead>
                  <tr>
                    <th style={{ width: 36, textAlign: "center" }}></th>
                    <th>PACKAGE NAME &amp; DETAILS</th>
                    <th style={{ width: 130, textAlign: "center" }}>CONFIGURED CARS</th>
                    <th style={{ width: 90, textAlign: "center" }}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((pkg) => {
                    const open = expanded.includes(pkg.key);
                    const partyOverridesCount = customFaresByPkg[pkg.name]?.length || 0;
                    const vendorOverridesCount = customFaresByPkg[pkg.name]?.length || 0;
                    return (
                      <React.Fragment key={pkg.key}>
                        <tr
                          className={`sf-pkg-row ${open ? "sf-row-active" : ""}`}
                          onClick={() => toggle(pkg.key)}
                        >
                          <td className="sf-pkg-toggle text-center">
                            <i className={`fa-solid fa-chevron-${open ? "down" : "right"}`}></i>
                          </td>
                          <td className="sf-pkg-name-td">
                            <span className="sf-pkg-title">{pkg.name}</span>
                            {pkg.packageType === 1 ? (
                              <span className="pill-badge sf-badge-km">
                                <i className="fa-solid fa-road me-1"></i> Outstation
                              </span>
                            ) : (
                              <span className="pill-badge sf-badge-day">
                                <i className="fa-solid fa-sun me-1"></i> Local
                              </span>
                            )}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            <div className="sf-badge-wrapper">
                              <span
                                className="sf-pkg-badge"
                                title={pkg.fares.map((f) => vtName(f.Vtype, f.VehicleTypeName)).join(", ")}
                              >
                                <i className="fa-solid fa-car me-1"></i>
                                {pkg.fares.length} {pkg.fares.length === 1 ? "vehicle" : "vehicles"}
                              </span>
                              {pkg.fares.length > 0 && (
                                <div className="sf-tooltip-popover">
                                  <div className="sf-tooltip-header">Configured Cars</div>
                                  {pkg.fares.map((f, i) => (
                                    <div key={i} className="sf-tooltip-item">
                                      <i className="fa-solid fa-car-side"></i> {vtName(f.Vtype, f.VehicleTypeName)}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="sf-pkg-actions text-center" onClick={(e) => e.stopPropagation()}>
                            <div className="ledger_actions_wrapper">
                              <button
                                type="button"
                                className="ledger_btn_edit"
                                onClick={(e) => edit(pkg, e)}
                                title="Edit Package Rates"
                              >
                                <i className="fa-solid fa-pen-to-square"></i>
                              </button>
                              <button
                                type="button"
                                className="ledger_btn_delete"
                                onClick={(e) => confirmDel(pkg, e)}
                                title="Delete Package"
                              >
                                <i className="fa-solid fa-trash-can"></i>
                              </button>
                            </div>
                          </td>
                        </tr>

                        {open && (
                          <tr className="sf-sub-row">
                            <td colSpan={4}>
                              <div className="sf-sub-wrap">
                                {pkg.fares.length > 0 ? (
                                  <table className="sf-sub-table">
                                    <colgroup>
                                      {pkg.packageType === 1 ? (
                                        <>
                                          <col style={{ width: "34%" }} />
                                          <col style={{ width: "22%" }} />
                                          <col style={{ width: "22%" }} />
                                          <col style={{ width: "22%" }} />
                                        </>
                                      ) : (
                                        <>
                                          <col style={{ width: "22%" }} />
                                          <col style={{ width: "13%" }} />
                                          <col style={{ width: "13%" }} />
                                          <col style={{ width: "13%" }} />
                                          <col style={{ width: "13%" }} />
                                          <col style={{ width: "13%" }} />
                                          <col style={{ width: "13%" }} />
                                        </>
                                      )}
                                    </colgroup>
                                    <thead>
                                      {pkg.packageType === 1 ? (
                                        <>
                                          <tr>
                                            <th rowSpan="2" className="sf-th-vtype sf-col-left">Vehicle Type</th>
                                            <th colSpan="1" className="sf-group-header sf-group-base">INCLUDED PACKAGE</th>
                                            <th colSpan="2" className="sf-group-header sf-group-extra">ADDITIONAL RATES (EXTRA)</th>
                                          </tr>
                                          <tr>
                                            <th className="sf-th-base sf-th-base-first sf-th-base-last sf-col-center">Incl. KM</th>
                                            <th className="sf-th-extra sf-th-extra-first sf-col-center">Fuel Rate/KM</th>
                                            <th className="sf-th-extra sf-th-extra-last sf-col-center">Lub Rate/KM</th>
                                          </tr>
                                        </>
                                      ) : (
                                        <>
                                          <tr>
                                            <th rowSpan="2" className="sf-th-vtype sf-col-left">Vehicle Type</th>
                                            <th colSpan="3" className="sf-group-header sf-group-base">INCLUDED PACKAGE</th>
                                            <th colSpan="3" className="sf-group-header sf-group-extra">ADDITIONAL RATES (EXTRA)</th>
                                          </tr>
                                          <tr>
                                            <th className="sf-th-base sf-th-base-first sf-col-center">Base Fare</th>
                                            <th className="sf-th-base sf-col-center">Incl. Hrs</th>
                                            <th className="sf-th-base sf-th-base-last sf-col-center">Incl. KM</th>
                                            <th className="sf-th-extra sf-th-extra-first sf-col-center">Fuel Rate/KM</th>
                                            <th className="sf-th-extra sf-col-center">Lub Rate/KM</th>
                                            <th className="sf-th-extra sf-th-extra-last sf-col-center">Time Rate/Hr</th>
                                          </tr>
                                        </>
                                      )}
                                    </thead>
                                    <tbody>
                                      {pkg.fares.map((f, i) => (
                                        <tr key={f.id || f.ID || i}>
                                          <td className="sf-td-vtype sf-col-left">
                                            {vtName(f.Vtype, f.VehicleTypeName)}
                                          </td>
                                          {pkg.packageType === 1 ? (
                                            <>
                                              <td className="sf-td-base sf-th-base-first sf-th-base-last sf-col-center">{fmt(f.IncludedKM)}</td>
                                              <td className="sf-td-extra sf-td-extra-first sf-col-center">₹{fmt(f.FuelRateKM)}</td>
                                              <td className="sf-td-extra sf-th-extra-last sf-col-center">₹{fmt(f.LubRateKM)}</td>
                                            </>
                                          ) : (
                                            <>
                                              <td className="sf-td-base sf-td-base-first sf-col-center"><span className="sf-currency">₹</span>{fmt(f.BaseFare)}</td>
                                              <td className="sf-td-base sf-col-center">{fmt(f.IncludedHrs)} hrs</td>
                                              <td className="sf-td-base sf-th-base-last sf-col-center">{fmt(f.IncludedKM)} km</td>
                                              <td className="sf-td-extra sf-td-extra-first sf-col-center">₹{fmt(f.FuelRateKM)}</td>
                                              <td className="sf-td-extra sf-col-center">₹{fmt(f.LubRateKM)}</td>
                                              <td className="sf-td-extra sf-th-extra-last sf-col-center">₹{fmt(f.TimeRatePM)}</td>
                                            </>
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                ) : (
                                  <div className="text-muted p-2" style={{ fontSize: "11px", fontStyle: "italic" }}>
                                    No rates configured for this package.
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="sf-pagination">
                <button
                  type="button"
                  className="dash-page-btn"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  <i className="fa-solid fa-chevron-left me-1"></i> Prev
                </button>
                <span className="sf-page-info">
                  Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> ({grouped.length} packages)
                </span>
                <button
                  type="button"
                  className="dash-page-btn"
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next <i className="fa-solid fa-chevron-right ms-1"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        title="Delete Fare Package?"
        message="Are you sure you want to delete this fare package and all associated vehicle rates?"
        itemName={deletePackageInfo?.packageName}
        loading={isDeleting}
      />
    </div>
  );
}

export default ShowFares;

